<script>
import { usePlatform } from '@monogrid/vue3-boost/functions'
import gsap from 'gsap'
import { defineComponent, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import RightArrow from '@/assets/rightArrow.svg'
import ExpandableText from '@/atoms/ExpandableText.vue'
import CustomizedBtn from '@/atoms/CustomizedBtn.vue'
export default defineComponent({
  name: 'SectionProduct',
  components: {
    RightArrow,
    ExpandableText,
    CustomizedBtn
  },
  props: {
    product: { type: Object, default: () => ({}) },
    element: { type: Object, required: true },
    startTime: { type: Number, required: true },
    endTime: { type: Number, required: true },
    align: { type: String, required: true }
  },
  setup (props, { expose, emit, attrs }) {
    const item = ref()
    const router = useRouter()
    const HomePage = inject('HomePage')
    const platform = usePlatform()
    const maxHeight = 35
    const isProduct = props.element._repeaterType === 'product'

    let smallDevice = false

    if (platform.ios.value === true) {
      smallDevice = platform.iphone.value
    } else {
      smallDevice = platform.mobile.value
      if (platform.tablet.value === true) {
        smallDevice = false
      }
    }

    const animateSection = (timeline) => {
      const elements = item.value.querySelectorAll('.element')
      const startTime = props.startTime
      const endTime = props.endTime
      const duration = (endTime - startTime) / 2
      const animDuration = duration * 0.9
      const elementTime = animDuration / elements.length
      const labelName = isProduct ? props.element.product.name : props.element.name
      timeline.fromTo(item.value, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.01 }, startTime)
      timeline.addLabel(labelName, startTime + duration)
      timeline.add(() => {
        router.push({ hash: '#' + labelName, params: { skipScroll: true } })
      }, startTime + duration)
      timeline.fromTo(elements, { autoAlpha: 0, y: 10, skewY: 6 }, { autoAlpha: 1, y: 0, skewY: 0, duration: elementTime, stagger: elementTime / 2 }, startTime)
      timeline.to(elements, { autoAlpha: 0, duration: animDuration }, endTime - animDuration)
      timeline.to(item.value, { autoAlpha: 0, duration: 0.01 }, endTime - 0.01)
    }

    let timeline

    const animateSectionBackground = (isExpanding) => {
      if (!platform.mobile) return

      timeline = gsap.timeline()
      if (isExpanding) {
        timeline.fromTo('.item', { background: 'rgba(25, 24, 26, 0)' }, { background: 'rgba(25, 24, 26, 0.8)', duration: 1 }, 0)
      } else {
        timeline.fromTo('.item', { background: 'rgba(25, 24, 26, 0.8)' }, { background: 'rgba(25, 24, 26, 0)', duration: 1 }, 0)
      }
    }

    expose({ animateSection })

    const expanding = (isExpanding) => {
      animateSectionBackground(isExpanding)
    }

    return {
      item,
      HomePage,
      maxHeight,
      isProduct,
      smallDevice: smallDevice,
      mobile: platform.mobile,
      expanding
    }
  }
})
</script>

<template>
  <section ref="item" class="item flex w-full pt-20" :class="{ ['items-end pb-25'] : mobile, ['items-center'] : !mobile}">
    <div
      class="flex w-full"

      :class="{
        'justify-center text-center pl-10 md:pl-22' : !align || align==='center' && !mobile,
        'justify-start pl-10 pr-0 md:pl-23 smallHeight:pr-20' : align==='left' && !mobile,
        'justify-end pr-10 pl-0 md:pr-23 smallHeight:pl-20' : align === 'right' && !mobile,
        'justify-center px-5 md:px-20' : mobile,
      } "
    >
      <div class="wrapper md:px-5 lg:px-0">
        <div v-if="isProduct" class="header flex items-center">
          <CustomizedBtn
            :href="element.product.productLink"
            target="_blank"
            rel="noopener"
            class="element"
            bordered
            icon
            content
            min-width
          >
            <template #content>
              {{ element.product.name }}
            </template>
            <template #icon>
              <RightArrow />
            </template>
          </CustomizedBtn>
        </div>
        <div ref="content" class="content">
          <h1 v-if="isProduct" class="element c-gold h2">{{ element.product.title }}</h1>
          <h1 v-else class="element c-gold h2">{{ element.title }}</h1>

          <ExpandableText
            v-if="smallDevice"
            :expand="HomePage.expandLabel"
            :collapse="HomePage.collapseLabel"
            :height="maxHeight"
            :color="'#C6B98F'"
            class="element"
            @expanding="expanding"
          >
            <p v-if="isProduct">{{ element.product.text }}</p>
            <p v-else>{{ element.text }}</p>
          </ExpandableText>
          <p v-else-if="!smallDevice && isProduct" class="element">{{ element.product.text }}</p>
          <p v-else-if="!smallDevice && !isProduct" class="element">{{ element.text }}</p>

          <div v-if="!isProduct" class="promo element">
            <h4 class="element c-gold font-bold">{{ element.promoTitle }}</h4>
            <CustomizedBtn
              :href="element.ctaLink"
              target="_blank"
              rel="noopener"
              class="element"
              underlined
              content
              :icon="false"
              :bordered="false"
            >
              <template #content>
                {{ element.ctaLabel }}
              </template>
            </CustomizedBtn>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
section {
  z-index: $z-content;
  opacity: 0;
  visibility: hidden;
}

.wrapper {
  max-width: 100%;

  html:not(.mobile) & {
    @screen md {
      max-width: 60%;
    }

    @screen lg {
      max-width: 50%;
    }
  }
}

.header {
  max-width: 100%;

  @screen xl {
    max-width: 90%;
  }
}

.element {
  transform-origin: top left;
}

.item {
  background: rgba(25, 24, 26, 0%);
}

.content {
  > * {
    margin: rem(20px 0);
  }

  h1 {
    margin: rem(20px 0);
    line-height: 1;
  }

  @screen md {
    > * {
      margin: rem(41px) 0;
    }
  }

  @screen smallHeight {
    > * {
      margin: rem(15px) 0;
    }
  }
}

p {
  max-width: 100%;

  html:not(.mobile) & {
    @screen lg {
      max-width: rem(600px);
    }
  }

  @screen smallHeight {
    max-width: 100%;
  }
}

.product-text {
  bottom: rem(20px);
  overflow: hidden;

  @screen md {
    height: 100%;
    max-height: 100%;
  }
}

.resized-text {
  max-height: 35px;

  @screen md {
    max-height: 100%;
  }
}

.promo {
  h4 {
    margin: rem(10px 0);
  }

  @screen smallHeight {
    margin: rem(20px 0);
  }
}
</style>
